import React from "react";
import { Box, Paper, Avatar, Typography, Chip, Grow } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Person as UserIcon,
  SmartToy as BotIcon,
  AutoAwesome as SparklesIcon
} from "@mui/icons-material";

const MessageBubble = ({ role, content, timestamp, isTyping, intent }) => {
  const theme = useTheme();
  const isUser = role === "user";

  const getIntentBadge = () => {
    if (!intent || isUser) return null;
    
    const intentLabels = {
      employee_count_query: "Nombre d'employés",
      employee_list_query: "Liste employés",
      employee_manager_query: "Manager",
      employee_details_query: "Détails employé",
      leave_pending_query: "Congés en attente",
      leave_approved_query: "Congés approuvés",
      leave_balance_query: "Solde congé",
      job_offer_location_query: "Offres par lieu",
      job_offer_skill_query: "Offres par compétence",
      ranking_top_query: "Top candidats",
      holiday_date_query: "Jours fériés",
      greeting_query: "Salutation",
      thanks_query: "Remerciements",
      platform_capabilities_query: "Fonctionnalités",
      rag_explanation_query: "Explication RAG",
    };

    const label = intentLabels[intent] || intent.replace(/_/g, ' ').replace(/query/g, '');
    
    return (
      <Chip
        icon={<SparklesIcon sx={{ fontSize: '14px !important' }} />}
        label={<Typography sx={{ textTransform: 'capitalize', fontSize: '0.75rem', fontWeight: 600 }}>{label}</Typography>}
        size="small"
        sx={{
          bgcolor: 'primary.50',
          color: 'primary.700',
          mb: 1.5,
          '& .MuiChip-icon': { color: 'primary.700' }
        }}
      />
    );
  };

  return (
    <Grow in={true} timeout={400} style={{ transformOrigin: isUser ? 'right center' : 'left center' }}>
      <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 2, 
          maxWidth: { xs: '90%', md: '85%' },
          flexDirection: isUser ? 'row-reverse' : 'row'
        }}>
          
          {/* Avatar */}
          <Avatar sx={{ 
            bgcolor: isUser ? 'primary.main' : 'common.white',
            color: isUser ? 'white' : 'primary.main',
            boxShadow: theme.shadows[2],
            width: 40,
            height: 40
          }}>
            {isUser ? <UserIcon /> : <BotIcon />}
          </Avatar>

          {/* Message Content */}
          <Paper 
            elevation={isUser ? 2 : 1}
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderTopRightRadius: isUser ? 0 : 24,
              borderTopLeftRadius: isUser ? 24 : 0,
              bgcolor: isUser ? 'primary.main' : 'background.paper',
              color: isUser ? 'primary.contrastText' : 'text.primary',
              position: 'relative',
              overflow: 'hidden',
              minWidth: 100,
              border: isUser ? 'none' : '1px solid',
              borderColor: 'grey.200'
            }}
          >
            {isTyping ? (
              <Box sx={{ display: 'flex', gap: 0.5, py: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ width: 6, height: 6, bgcolor: 'grey.400', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both' }} />
                <Box sx={{ width: 6, height: 6, bgcolor: 'grey.400', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.16s' }} />
                <Box sx={{ width: 6, height: 6, bgcolor: 'grey.400', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.32s' }} />
                <style>
                  {`
                    @keyframes bounce {
                      0%, 80%, 100% { transform: scale(0); }
                      40% { transform: scale(1); }
                    }
                  `}
                </style>
              </Box>
            ) : (
              <Box className="prose prose-sm max-w-none">
                {getIntentBadge()}
                {typeof content === 'string' ? (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {content}
                  </Typography>
                ) : (
                  content
                )}
              </Box>
            )}

            {/* Timestamp */}
            {timestamp && !isTyping && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  mt: 1.5,
                  textAlign: 'right',
                  color: isUser ? 'rgba(255,255,255,0.7)' : 'text.disabled'
                }}
              >
                {new Date(timestamp).toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            )}
          </Paper>
        </Box>
      </Box>
    </Grow>
  );
};

export default MessageBubble;
